import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter, Optional } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';

// Declare PDF.js types for CDN loading
declare const pdfjsLib: any;

// Static cache to store blob URLs by original URL to prevent re-downloading
interface CachedPdf {
  blobUrl: string;
  sanitizedUrl: SafeResourceUrl;
  timestamp: number;
}

@Component({
  selector: 'app-image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss']
})
export class ImageViewerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() imageUrl: string = '';
  @Input() title: string = 'Image Viewer';
  @Input() fileType: string = '';
  @Input() allowUpload: boolean = false; // New input to enable upload functionality
  @Input() isInline: boolean = false; // Flag to indicate if used as inline component (not modal)
  @Output() fileSelected = new EventEmitter<File>(); // Emit selected file
  
  // Zoom properties
  zoomLevel: number = 1;
  minZoom: number = 0.5;
  maxZoom: number = 5;
  panX: number = 0;
  panY: number = 0;
  isDragging: boolean = false;
  dragStartX: number = 0;
  dragStartY: number = 0;
  dragStartPanX: number = 0;
  dragStartPanY: number = 0;
  
  pdfUrl: SafeResourceUrl | null = null;
  googleDocsViewerUrl: SafeResourceUrl | null = null;
  sanitizedImageUrl: SafeResourceUrl | null = null; // Sanitized image URL for blob URLs
  pdfImageUrl: SafeResourceUrl | null = null; // PDF rendered as image
  isLoading: boolean = false;
  previewUrl: string | null = null; // Preview URL for uploaded image
  selectedFile: File | null = null; // Selected file
  showPdfAsImage: boolean = false; // Flag to show PDF as image
  imageLoadError: boolean = false; // Flag to track image load errors
  pdfPreviewImageUrl: string | null = null; // PDF rendered as PNG image (data URL)
  private blobUrl: string | null = null;
  private pdfjsLibLoaded: boolean = false; // Flag to track if PDF.js is loaded
  
  // Static cache shared across all instances
  private static pdfCache: Map<string, CachedPdf> = new Map();
  private static readonly CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes

  constructor(
    @Optional() public activeModal: NgbActiveModal | null, // Make optional for inline usage
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {
    // Load PDF.js from CDN
    this.loadPdfJs();
  }

  /**
   * Load PDF.js library from CDN
   */
  private loadPdfJs(): void {
    if (this.pdfjsLibLoaded || (window as any).pdfjsLib) {
      this.pdfjsLibLoaded = true;
      this.configurePdfJsWorker();
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="pdf.js"]')) {
      // Wait for it to load
      const checkInterval = setInterval(() => {
        if ((window as any).pdfjsLib) {
          clearInterval(checkInterval);
          this.pdfjsLibLoaded = true;
          this.configurePdfJsWorker();
        }
      }, 100);
      return;
    }

    // Load PDF.js from CDN
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.onload = () => {
      this.pdfjsLibLoaded = true;
      this.configurePdfJsWorker();
    };
    script.onerror = () => {
      console.error('Failed to load PDF.js from CDN');
    };
    document.head.appendChild(script);
  }

  /**
   * Configure PDF.js worker
   */
  private configurePdfJsWorker(): void {
    if ((window as any).pdfjsLib) {
      (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    }
  }

  /**
   * Get PDF.js library instance
   */
  private getPdfJsLib(): any {
    return (window as any).pdfjsLib;
  }

  ngOnInit(): void {
    this.initializeImageUrl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If imageUrl or fileType changes, re-initialize
    if ((changes['imageUrl'] && !changes['imageUrl'].firstChange) || 
        (changes['fileType'] && !changes['fileType'].firstChange)) {
      this.initializeImageUrl();
    }
  }

  private initializeImageUrl(): void {
    // Reset flags
    this.showPdfAsImage = false;
    this.pdfImageUrl = null;
    this.pdfPreviewImageUrl = null;
    this.imageLoadError = false;
    
    // If imageUrl is provided, use it for display
    if (this.imageUrl) {
      // Remove 'unsafe:' prefix if present (Angular adds this when sanitizing)
      const cleanUrl = this.imageUrl.replace(/^unsafe:/, '');
      
      // Set previewUrl for blob URLs
      if (cleanUrl.startsWith('blob:')) {
        this.previewUrl = cleanUrl;
      }
      
      // Check if it's a PDF (using fileType first, then URL)
      const isPdf = this.isPdfFile(cleanUrl);
      const isDoc = this.isDocFile(cleanUrl);
      
      if (isPdf) {
        // For blob URLs (uploaded PDFs), render as image preview
        if (cleanUrl.startsWith('blob:')) {
          // Try to render PDF as image preview
          this.renderPdfPreview(cleanUrl);
        } else {
          // For regular URLs (server PDFs), use Google Docs viewer
          this.setupGoogleDocsViewer();
        }
      } else if (isDoc) {
        // Use Google Docs viewer for DOC files
        this.setupGoogleDocsViewer();
      } else {
        // For images, set preview URL and sanitize if it's a blob URL
        this.previewUrl = cleanUrl;
        this.sanitizeImageUrl();
      }
    }
  }
  
  setupGoogleDocsViewer(): void {
    let urlToUse = this.previewUrl || this.imageUrl;
    if (!urlToUse) return;
    
    // Remove 'unsafe:' prefix if present (Angular adds this when sanitizing)
    urlToUse = urlToUse.replace(/^unsafe:/, '');
    
    // For blob URLs (newly uploaded files)
    if (urlToUse.startsWith('blob:')) {
      if (this.isPdfFile(urlToUse) || this.fileType === 'application/pdf') {
        // Use direct blob URL for PDF viewing
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlToUse);
        this.isLoading = false;
      } else if (this.isDocFile(urlToUse) || (this.fileType && (this.fileType.includes('msword') || this.fileType.includes('wordprocessingml')))) {
        // DOC files with blob URLs cannot be previewed with Google Docs viewer
        // They need to be uploaded to server first
        // For now, we'll try to use the blob URL directly (may not work in all browsers)
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlToUse);
        this.isLoading = false;
      }
    } else {
      // For regular URLs (server URLs), use Google Docs viewer
      // Source - https://stackoverflow.com/a
      // Posted by ThanHtutZaw
      // Retrieved 2026-01-06, License - CC BY-SA 4.0
      const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(urlToUse)}&embedded=true`;
      this.googleDocsViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    // Don't revoke blob URL here - it's cached and may be used by other instances
    // The cache will handle cleanup when entries expire
    this.blobUrl = null;
    // Revoke preview URL if it's a blob URL
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  /**
   * Handle file selection for upload
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.selectedFile = file;
      
      // Create preview URL
      if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewUrl);
      }
      
      this.previewUrl = URL.createObjectURL(file);
      
      // Update fileType for PDF/DOC detection
      this.fileType = file.type;
      
      // Handle PDF/DOC files
      if (this.isPdfFile(file.name) || this.fileType === 'application/pdf') {
        // For uploaded PDFs (blob URLs), render as image preview
        this.renderPdfPreviewFromFile(file);
      } else if (this.isDocFile(file.name) || (this.fileType && (this.fileType.includes('msword') || this.fileType.includes('wordprocessingml')))) {
        this.setupGoogleDocsViewer();
      } else {
        // For images, sanitize the blob URL
        this.sanitizeImageUrl();
      }
      
      // Emit the selected file
      this.fileSelected.emit(file);
      
      // Reset input to allow selecting the same file again
      input.value = '';
    }
  }

  /**
   * Sanitize image URL if it's a blob URL
   */
  private sanitizeImageUrl(): void {
    const urlToUse = this.previewUrl || this.imageUrl;
    if (!urlToUse) {
      this.sanitizedImageUrl = null;
      return;
    }
    
    // Remove 'unsafe:' prefix if present (Angular adds this when sanitizing)
    const cleanUrl = urlToUse.replace(/^unsafe:/, '');
    
    // If it's a blob URL or any URL, sanitize it using DomSanitizer
    // This allows blob URLs to be displayed properly
    this.sanitizedImageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(cleanUrl);
  }

  /**
   * Get the display URL (preview or imageUrl)
   */
  getDisplayUrl(): string {
    return this.previewUrl || this.imageUrl || '';
  }

  /**
   * Get the sanitized image URL for use in img src
   */
  getSanitizedImageUrl(): SafeResourceUrl | string {
    if (this.sanitizedImageUrl) {
      return this.sanitizedImageUrl;
    }
    // Fallback to regular URL if not sanitized yet
    return this.getDisplayUrl();
  }

  /**
   * Render PDF preview as image from blob URL
   */
  private async renderPdfPreview(blobUrl: string): Promise<void> {
    try {
      // Fetch the blob and convert to File
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      const file = new File([blob], 'preview.pdf', { type: 'application/pdf' });
      await this.renderPdfPreviewFromFile(file);
    } catch (error) {
      console.error('Error rendering PDF preview from blob URL:', error);
      // Fallback to object tag
      this.setupPdfAsImage(blobUrl);
    }
  }

  /**
   * Render PDF preview as image from File object
   */
  private async renderPdfPreviewFromFile(file: File): Promise<void> {
    try {
      // Wait for PDF.js to be loaded
      if (!this.pdfjsLibLoaded) {
        await this.waitForPdfJs();
      }

      const pdfjsLib = this.getPdfJsLib();
      if (!pdfjsLib) {
        throw new Error('PDF.js library not loaded');
      }

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport }).promise;
      this.pdfPreviewImageUrl = canvas.toDataURL('image/png');
      this.showPdfAsImage = true;
    } catch (error) {
      console.error('Error rendering PDF preview:', error);
      // Fallback to object tag
      if (this.previewUrl) {
        this.setupPdfAsImage(this.previewUrl);
      }
    }
  }

  /**
   * Wait for PDF.js library to load
   */
  private async waitForPdfJs(): Promise<void> {
    return new Promise((resolve, reject) => {
      const maxAttempts = 50; // 5 seconds max wait
      let attempts = 0;
      const checkInterval = setInterval(() => {
        attempts++;
        if ((window as any).pdfjsLib || this.pdfjsLibLoaded) {
          clearInterval(checkInterval);
          this.pdfjsLibLoaded = true;
          this.configurePdfJsWorker();
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          reject(new Error('PDF.js library failed to load'));
        }
      }, 100);
    });
  }

  /**
   * Set up PDF as image for blob URLs (fallback method)
   * For blob URLs, we can use object tag with type="application/pdf" which browsers can render
   */
  private setupPdfAsImage(url: string): void {
    if (!url || !url.startsWith('blob:')) {
      return;
    }
    
    // Remove 'unsafe:' prefix if present
    const cleanUrl = url.replace(/^unsafe:/, '');
    
    // For blob URLs, sanitize and set as PDF image URL
    // Browsers can render PDFs in object/embed tags, but for img tags we need to use object
    this.pdfImageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(cleanUrl);
    this.showPdfAsImage = true;
  }

  /**
   * Check if we should show PDF as image preview (rendered PNG)
   */
  shouldShowPdfAsImage(): boolean {
    // Check if we have a rendered PDF preview image
    if (this.pdfPreviewImageUrl && this.showPdfAsImage) {
      return true;
    }
    
    // Fallback: check if we have pdfImageUrl set and flag enabled (for object tag)
    if (this.pdfImageUrl && this.showPdfAsImage) {
      return true;
    }
    
    return false;
  }

  /**
   * Check if we should show PDF preview as rendered image (PNG)
   */
  shouldShowPdfPreviewImage(): boolean {
    return !!(this.pdfPreviewImageUrl && this.showPdfAsImage);
  }

  isPdfFile(url: string): boolean {
    if (!url) return false;
    // Check fileType input first (for newly uploaded files)
    if (this.fileType === 'application/pdf') {
      return true;
    }
    const urlLower = url.toLowerCase();
    return urlLower.endsWith('.pdf') || urlLower.includes('.pdf?') || urlLower.includes('application/pdf');
  }
  
  isDocFile(url: string): boolean {
    if (!url) return false;
    // Check fileType input first (for newly uploaded files)
    if (this.fileType && (this.fileType.includes('msword') || this.fileType.includes('wordprocessingml'))) {
      return true;
    }
    const urlLower = url.toLowerCase();
    return urlLower.includes('.doc') || urlLower.includes('.docx') || 
           urlLower.includes('msword') || urlLower.includes('wordprocessingml');
  }

  isImageFile(url: string): boolean {
    if (!url) return false;
    // Check fileType for images
    if (this.fileType && this.fileType.startsWith('image/')) {
      return true;
    }
    const urlLower = url.toLowerCase();
    return urlLower.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i) !== null;
  }

  loadPdfAsBlob(): void {
    if (!this.imageUrl) return;
    
    // Check cache first
    const cached = ImageViewerComponent.pdfCache.get(this.imageUrl);
    if (cached && (Date.now() - cached.timestamp) < ImageViewerComponent.CACHE_EXPIRY) {
      // Use cached blob URL
      this.blobUrl = cached.blobUrl;
      this.pdfUrl = cached.sanitizedUrl;
      this.isLoading = false;
      return;
    }
    
    // Clean up expired cache entry if exists
    if (cached) {
      URL.revokeObjectURL(cached.blobUrl);
      ImageViewerComponent.pdfCache.delete(this.imageUrl);
    }
    
    this.isLoading = true;
    
    // Fetch PDF as blob to bypass content-disposition header
    this.http.get(this.imageUrl, { 
      responseType: 'blob' 
    }).subscribe({
      next: (blob: Blob) => {
        // Create blob URL from the fetched blob
        this.blobUrl = URL.createObjectURL(blob);
        // Sanitize the blob URL
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
        
        // Cache the blob URL for future use
        ImageViewerComponent.pdfCache.set(this.imageUrl, {
          blobUrl: this.blobUrl,
          sanitizedUrl: this.pdfUrl,
          timestamp: Date.now()
        });
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading PDF:', error);
        this.isLoading = false;
        // Fallback to original URL if blob fetch fails
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.imageUrl);
      }
    });
  }

  /**
   * Handle image load error
   */
  onImageError(): void {
    this.imageLoadError = true;
    console.error('Failed to load image:', this.getDisplayUrl());
  }

  close() {
    // Don't revoke blob URL here - it's cached
    this.blobUrl = null;
    if (this.activeModal) {
      this.activeModal.dismiss();
    }
  }

  /**
   * Zoom in (centered)
   */
  zoomIn(): void {
    if (this.zoomLevel < this.maxZoom) {
      const oldZoom = this.zoomLevel;
      this.zoomLevel = Math.min(this.zoomLevel + 0.25, this.maxZoom);
      // Adjust pan to keep center focused
      this.adjustPanForCenterZoom(oldZoom, this.zoomLevel);
    }
  }

  /**
   * Zoom out (centered)
   */
  zoomOut(): void {
    if (this.zoomLevel > this.minZoom) {
      const oldZoom = this.zoomLevel;
      this.zoomLevel = Math.max(this.zoomLevel - 0.25, this.minZoom);
      // Adjust pan to keep center focused
      this.adjustPanForCenterZoom(oldZoom, this.zoomLevel);
      // Reset pan if zoomed out to minimum
      if (this.zoomLevel === this.minZoom) {
        this.panX = 0;
        this.panY = 0;
      }
    }
  }

  /**
   * Adjust pan to maintain center focus when zooming
   */
  private adjustPanForCenterZoom(oldZoom: number, newZoom: number): void {
    // Calculate the zoom change ratio
    const zoomRatio = newZoom / oldZoom;
    // Adjust pan to keep the center point fixed
    // When zooming in, we need to move the pan to compensate
    this.panX = this.panX * zoomRatio;
    this.panY = this.panY * zoomRatio;
  }

  /**
   * Reset zoom and pan
   */
  resetZoom(): void {
    this.zoomLevel = 1;
    this.panX = 0;
    this.panY = 0;
  }

  /**
   * Handle mouse wheel zoom (centered)
   */
  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    const oldZoom = this.zoomLevel;
    const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel + delta));
    
    if (newZoom !== this.zoomLevel) {
      this.zoomLevel = newZoom;
      // Adjust pan to keep center focused
      this.adjustPanForCenterZoom(oldZoom, newZoom);
    }
  }

  /**
   * Handle mouse down for panning
   */
  onMouseDown(event: MouseEvent): void {
    if (this.zoomLevel > 1 && event.button === 0) { // Left mouse button only
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartPanX = this.panX;
      this.dragStartPanY = this.panY;
      event.preventDefault();
    }
  }

  /**
   * Handle mouse move for panning
   */
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;
      this.panX = this.dragStartPanX + deltaX / this.zoomLevel;
      this.panY = this.dragStartPanY + deltaY / this.zoomLevel;
      event.preventDefault();
    }
  }

  /**
   * Handle mouse up to stop panning
   */
  onMouseUp(event: MouseEvent): void {
    this.isDragging = false;
  }

  // Static method to clear expired cache entries (can be called periodically)
  static clearExpiredCache(): void {
    const now = Date.now();
    for (const [url, cached] of ImageViewerComponent.pdfCache.entries()) {
      if (now - cached.timestamp > ImageViewerComponent.CACHE_EXPIRY) {
        URL.revokeObjectURL(cached.blobUrl);
        ImageViewerComponent.pdfCache.delete(url);
      }
    }
  }

  // Static method to clear all cache
  static clearAllCache(): void {
    for (const cached of ImageViewerComponent.pdfCache.values()) {
      URL.revokeObjectURL(cached.blobUrl);
    }
    ImageViewerComponent.pdfCache.clear();
  }
}

